import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Box from "veneer/Box";

import {
  MEDIA_ENTRY_APPLICATION,
  MEDIA_ENTRY_CONTENT_GROUP,
  MEDIA_ENTRY_FOLDER,
  MEDIA_ENTRY_USER_CONTENT,
  MEDIA_ENTRY_WEBPAGE,
} from "cortex/ui/src/fleet/constant/mediaLibrary";
import type { Media } from "cortex/ui/src/fleet/view/media-library/model";
import { isVideo } from "cortex/ui/src/fleet/view/media-library/utils";
import { type SidebarDrawerActions } from "cortex/ui/src/ui/sidebar";

import { ContentEditor } from "./ContentEditor";
import { ContentGroupEditor } from "./ContentGroupEditor";
import {
  type ChildFormState,
  type EditContentFormState,
  type FormConfig,
} from "./sidebarTypes";

type EditAction = (mediaEntry: Media, message: string) => void;
type FormStateGetter = () => ChildFormState;
type BaseConfig = Record<string, FormConfig>;

interface SidebarContentEditorProps {
  categories?: ReadonlyArray<string>;
  editContent?: EditAction;
  editContentGroup?: EditAction;
  hideFullscreen?: () => void;
  hideSidebar?: () => void;
  isStrategy?: boolean;
  mediaEntry?: Partial<Media>;
  setDrawerActions?: (actions: SidebarDrawerActions | null) => void;
  showFullscreen?: (
    url: string,
    mediaType: "image" | "video" | null,
    filename?: string,
  ) => void;
  strategyEditContent?: EditAction;
}

export function genBaseConfig(isStrategy?: boolean): BaseConfig {
  const config = {
    [MEDIA_ENTRY_APPLICATION]: {
      readOnlyFields: ["name"],
      excludeFields: ["url", "duration", "category", "conflictsWith"],
      editAction: null,
    },
    [MEDIA_ENTRY_CONTENT_GROUP]: {
      readOnlyFields: isStrategy ? ["name"] : [],
      excludeFields: [],
      editAction: null,
    },
    [MEDIA_ENTRY_FOLDER]: {
      readOnlyFields: [],
      excludeFields: [],
      editAction: null,
    },
    [MEDIA_ENTRY_USER_CONTENT]: {
      readOnlyFields: isStrategy ? ["name", "category"] : [],
      excludeFields: isStrategy ? ["url"] : ["url", "conflictsWith"],
      editAction: null,
    },
    [MEDIA_ENTRY_WEBPAGE]: {
      readOnlyFields: [],
      excludeFields: ["uploadDate", "category", "conflictsWith"],
      editAction: null,
    },
  };

  Object.values(config).forEach(Object.freeze);
  return Object.freeze(config) as BaseConfig;
}

export function SidebarContentEditor({
  categories = [],
  editContent = () => {},
  editContentGroup = () => {},
  hideFullscreen = () => {},
  hideSidebar = () => {},
  isStrategy,
  mediaEntry: mediaEntryProp = {},
  setDrawerActions,
  showFullscreen = () => {},
  strategyEditContent = () => {},
}: SidebarContentEditorProps) {
  const [mediaEntry, setMediaEntry] = useState<Partial<Media>>(mediaEntryProp);
  const childStates = useRef<Record<string, FormStateGetter>>({});
  const baseConfig = useMemo(() => genBaseConfig(isStrategy), [isStrategy]);

  useEffect(() => {
    setMediaEntry(mediaEntryProp);
  }, [mediaEntryProp]);

  const getChildState = useCallback(
    (componentName: string, getState: FormStateGetter) => {
      childStates.current[componentName] = getState;
    },
    [],
  );

  const collectState = useCallback(() => {
    return Object.keys(childStates.current).reduce<
      Record<string, ChildFormState>
    >((acc, key) => {
      const childState = childStates.current[key]();
      if (childState.type) {
        acc[childState.type] = { ...childState };
      }
      return acc;
    }, {});
  }, []);

  const getConfig = useCallback(
    (type?: string | null, mimeType?: string | null): FormConfig => {
      if (!type) {
        return {};
      }

      const config = { ...baseConfig[type] };

      if (!baseConfig[type]) {
        console.warn("Invalid type, no config exists.", type);
        return {};
      }

      switch (type) {
        case MEDIA_ENTRY_CONTENT_GROUP:
          config.editAction = isStrategy
            ? strategyEditContent
            : editContentGroup;
          break;

        case MEDIA_ENTRY_USER_CONTENT:
          config.editAction = isStrategy ? strategyEditContent : editContent;
          if (isVideo(mimeType)) {
            config.excludeFields = [
              ...(config.excludeFields ?? []),
              "duration",
            ];
          }
          break;

        case MEDIA_ENTRY_WEBPAGE:
          config.editAction = strategyEditContent;
          break;

        default:
          break;
      }

      return config;
    },
    [
      baseConfig,
      editContent,
      editContentGroup,
      isStrategy,
      strategyEditContent,
    ],
  );

  const saveEdits = useCallback(() => {
    const editableMediaEntry = { ...mediaEntry } as Media;
    const { content, type, mimeType } = editableMediaEntry;
    const allFormStates = collectState();
    const mediaEntryFormState = type ? allFormStates[type] : undefined;
    const config = getConfig(type, mimeType);

    if (type === MEDIA_ENTRY_CONTENT_GROUP && isStrategy) {
      const contentFormState = allFormStates[MEDIA_ENTRY_USER_CONTENT] as
        | EditContentFormState
        | undefined;
      content?.some((childContent) => {
        if (childContent.id === contentFormState?.id) {
          Object.assign(childContent, contentFormState);
          return true;
        }
        return false;
      });
    } else {
      Object.assign(editableMediaEntry, mediaEntryFormState);
    }

    config.editAction?.(editableMediaEntry, "Changes saved!");

    hideSidebar();
  }, [collectState, getConfig, hideSidebar, isStrategy, mediaEntry]);

  const { content, mimeType, type } = mediaEntry;
  const config = getConfig(type, mimeType);

  const drawerActions = useMemo<SidebarDrawerActions>(
    () => ({
      cancelLabel: "Cancel",
      commitDisabled: !config.editAction,
      commitLabel: "Save",
      dataTestId: {
        cancelButton: "sidebar-content-editor-cancel",
        commitButton: "sidebar-content-editor-submit",
      },
      onCancel: hideSidebar,
      onCommit: saveEdits,
    }),
    [config.editAction, hideSidebar, saveEdits],
  );

  useEffect(() => {
    setDrawerActions?.(drawerActions);

    return () => {
      setDrawerActions?.(null);
    };
  }, [drawerActions, setDrawerActions]);

  return (
    <Box dataTestId="sidebar-content-editor">
      {content ? (
        <ContentGroupEditor
          categories={categories}
          getChildState={getChildState}
          getConfig={getConfig}
          hideFullscreen={hideFullscreen}
          isStrategy={isStrategy}
          mediaEntry={mediaEntry}
          showFullscreen={showFullscreen}
        />
      ) : (
        <ContentEditor
          categories={categories}
          getChildState={getChildState}
          getConfig={getConfig}
          hideFullscreen={hideFullscreen}
          mediaEntry={mediaEntry}
          showFullscreen={showFullscreen}
        />
      )}
    </Box>
  );
}
